import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { Color, IconName, IconSize, IconStroke } from 'shared';
import classNames from 'classnames';

import { Type as IconButtonType } from 'ds/IconButton';
import { track } from 'lib/analytics';

import Icon from './Icon';
import IconButton from './IconButton';
import Layout from './Layout';
import Link from './Link';
import Text from './Text';
import { DS_CLASS_NAME, HEADER_Z_INDEX } from './constants';
import { useOnClickOutside } from './helpers';

export type EllipsisMenuItem = {
  label: string;
  icon?: IconName;
  iconElement?: JSX.Element;
  textColor?: Color;
  iconColor?: Color;
  section_id?: string | number | null;
} & (
  | {
      onClick: VoidFunction;
      href?: never;
    }
  | {
      href: string;
      onClick?: never;
    }
);

export interface Props {
  buttonType?: IconButtonType;
  size?: 'sm' | 'md' | 'lg';
  iconSize?: IconSize;
  iconStroke?: IconStroke;
  iconPosition?: 'left' | 'right';
  menuItems: EllipsisMenuItem[];
  disabled?: boolean;
  iconName?: IconName;
  menuItemHoverColor?: 'gray-50' | 'blue-50';
  onMenuChange?: (menuOpen: boolean) => void;
}

const EllipsisMenu: React.FC<Props> = ({
  buttonType = 'noBackground',
  size = 'lg',
  menuItems,
  disabled,
  iconSize = 'xs',
  iconStroke = 1.5,
  iconPosition = 'right',
  iconName = 'ellipsisH',
  menuItemHoverColor = 'blue-50',
  onMenuChange
}) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8
        }
      }
    ]
  });

  useOnClickOutside(
    menuRef,
    event => {
      if (popperElement && popperElement.contains(event.target as HTMLElement)) {
        return;
      }
      if (referenceElement && referenceElement.contains(event.target as HTMLElement)) {
        return;
      }

      setMenuOpen(false);
      onMenuChange && onMenuChange(false);
    },
    true // use mouseup/touchend so the full click completes before we check - avoids race on touch/slower devices
  );

  // Group items by section_id
  const ungroupedItems: EllipsisMenuItem[] = [];
  const sections = new Map<string | number, EllipsisMenuItem[]>();

  menuItems.forEach(item => {
    const sectionId = item.section_id;
    if (sectionId === null || sectionId === undefined) {
      ungroupedItems.push(item);
    } else {
      const sectionKey = String(sectionId);
      if (!sections.has(sectionKey)) {
        sections.set(sectionKey, []);
      }
      sections.get(sectionKey)!.push(item);
    }
  });

  const groupedItems = {
    ungrouped: ungroupedItems,
    sections: Array.from(sections.entries())
  };

  const renderMenuItem = (item: EllipsisMenuItem, index: number | string) => {
    const { label, icon, iconElement, href, onClick, textColor, iconColor } = item;
    const Content = (
      <Layout
        paddingY={12}
        paddingLeft={16}
        paddingRight={64}
        align="center"
        flexGrow={1}
        {...(iconPosition === 'left'
          ? { direction: 'row-reverse', justify: 'flex-end' }
          : { justify: 'space-between' })}
        __className={href ? `EllipsisMenu-menuItem is-hover-color-${menuItemHoverColor}` : undefined}
      >
        <Text size="body2" wrap={false} color={textColor as Color | undefined}>
          {label}
        </Text>
        {(icon || iconElement) && (
          <Layout {...(iconPosition === 'left' ? { marginRight: 12 } : { marginLeft: 24 })} display="inline-flex">
            {icon ? (
              <Icon name={icon} size={iconSize} stroke={iconStroke} color={(iconColor || 'black') as Color} />
            ) : (
              iconElement
            )}
          </Layout>
        )}
      </Layout>
    );

    return onClick ? (
      <button
        key={index}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          track('Element Interacted', { type: 'button', value: label, name: 'ellipsis' });
          onClick();
          setMenuOpen(false);
          onMenuChange && onMenuChange(false);
        }}
        className={classNames('EllipsisMenu-menuItem', `is-hover-color-${menuItemHoverColor}`)}
        style={{ display: 'block' }}
      >
        {Content}
      </button>
    ) : (
      <Link
        key={index}
        href={href}
        color="gray-900"
        onClick={() => {
          track('Element Interacted', { type: 'link', value: label, name: 'ellipsis' });
          setMenuOpen(false);
          onMenuChange && onMenuChange(false);
        }}
      >
        {Content}
      </Link>
    );
  };

  return (
    <>
      <Layout __className={classNames(DS_CLASS_NAME, 'EllipsisMenu', `is-size-${size}`)} position="relative">
        <div ref={setReferenceElement}>
          <IconButton
            type={buttonType}
            size={size}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              const newState = !menuOpen;
              setMenuOpen(newState);

              if (onMenuChange) {
                onMenuChange(newState);
              }
            }}
            name={iconName}
            disabled={disabled}
          />
        </div>
      </Layout>
      {menuOpen &&
        ReactDOM.createPortal(
          <div
            ref={element => {
              setPopperElement(element);
              menuRef.current = element;
            }}
            style={{
              ...styles.popper,
              zIndex: HEADER_Z_INDEX + 3
            }}
            {...attributes.popper}
          >
            <Layout
              paddingY={12}
              color="white"
              borderRadius={12}
              boxShadow="2px 0px 22px rgba(87, 73, 109, 0.12)"
              direction="column"
              flex
            >
              {groupedItems.ungrouped.map((item, index) => renderMenuItem(item, index))}
              {groupedItems.sections.map(([sectionId, items], sectionIndex) => (
                <React.Fragment key={sectionId}>
                  {sectionIndex > 0 || groupedItems.ungrouped.length > 0 ? (
                    <Layout borderTop borderColor="gray-50" marginY={4} />
                  ) : null}
                  {items.map((item, itemIndex) => renderMenuItem(item, `section-${sectionId}-${itemIndex}`))}
                </React.Fragment>
              ))}
            </Layout>
          </div>,
          document.body
        )}
    </>
  );
};

export default EllipsisMenu;
